import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  styled,
  useTheme,
} from '@mui/material'

import { useAppStore } from '@/store'
import { Chip } from '@/components/UI/Chip'

import { formatDate, replaceNullValues } from '@/utils/common'
import { IApplication } from '@/types'
import { useEffect, useState } from 'react'
import { LOAN_APPLICATION_STATUS } from '@/utils/constants'
import { ApplicationService } from '@/api/services/ApplicationService'

const statusToOptionsMap: { [key: string]: string[] } = {
  [LOAN_APPLICATION_STATUS.REFERRED]: ['APPROVED', 'DECLINED', 'CANCELED'],
  [LOAN_APPLICATION_STATUS.APPROVED]: ['PENDING_AGREEMENT'],
  [LOAN_APPLICATION_STATUS.PENDING_AGREEMENT]: ['PENDING_DISBURSEMENT', 'EXPIRED_AGREEMENT'],
  [LOAN_APPLICATION_STATUS.PENDING_DISBURSEMENT]: ['EXPIRED_AGREEMENT', 'DISBURSED'],
}

export const ApplicationSummary = () => {
  const { selectedApplication } = useAppStore()
  const [selectableStatuses, setSelectableStatuses] = useState<string[]>([])
  const [reviewStatus, setReviewStatus] = useState('')

  const theme = useTheme()
  const safeApplication = replaceNullValues<IApplication>(selectedApplication)

  useEffect(() => {
    if (safeApplication.status) {
      const status = safeApplication.status.toLowerCase()
      if (!statusToOptionsMap[status]) return
      setSelectableStatuses(statusToOptionsMap[status])
    }
  }, [selectedApplication])

  const handleSelectChange = (event: SelectChangeEvent) => {
    setReviewStatus(event.target.value)
  }

  const handleUpdateStatus = () => {
    ApplicationService.updateApplicationStatus(safeApplication.id, reviewStatus)
  }

  const fields = [
    {
      label: 'Application date',
      value: safeApplication.createdAt ? formatDate(safeApplication.createdAt) : '',
    },
    {
      label: 'Product',
      value: safeApplication.productName && (
        <Chip
          value={safeApplication.productName}
          variant="ghost"
          sx={{
            borderRadius: '6px',
            backgroundColor: '#fff',
            border: '1px solid #D0D5DD',
          }}
        />
      ),
    },
    {
      label: 'Loan principal',
      value: safeApplication.loanAmount ? `$${Number(safeApplication.loanAmount) / 100}` : '',
    },
    {
      label: 'Last update',
      value: safeApplication.updatedAt ? formatDate(safeApplication.updatedAt) : '',
    },
    { label: 'Loan fees', value: `${safeApplication.loanFeePercentage}% of principal` },
    {
      label: 'Purpose',
      value: `${safeApplication?.loanPurposeCategory} - ${safeApplication.loanPurposeSubcategory}`,
    },
    { label: 'Loan interest', value: `${safeApplication.loanInterestPercentage}% p.a.` },
    { label: 'Loan period', value: `${safeApplication.loanTermDays} days` },
  ]

  const renderStatusControl = () => {
    if (safeApplication.status) {
      const status = safeApplication.status.toLowerCase()
      if (!statusToOptionsMap[status]) return
      return (
        <Stack direction="row" gap={1} justifyContent="end" alignItems="center">
          <FormControl sx={{ m: 0, width: 150 }}>
            <Select
              size="small"
              value={reviewStatus}
              onChange={handleSelectChange}
              inputProps={{ 'aria-label': 'Without label' }}
              displayEmpty
            >
              <MenuItem value="" selected disabled>
                Select status
              </MenuItem>
              {selectableStatuses?.map(item => {
                return <MenuItem value={item}>{item}</MenuItem>
              })}
            </Select>
          </FormControl>
          <Button
            size="large"
            onClick={handleUpdateStatus}
            variant="contained"
            sx={{
              fontFamily: 'Inter',
              textTransform: 'capitalize',
              borderRadius: '6px',
              backgroundColor: '#2C063F',
              boxShadow: '0px 1px 2px 0px #1018280D',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#2C063F',
              },
            }}
          >
            Confirm
          </Button>
        </Stack>
      )
    }
  }

  return (
    <Stack gap={1} mb={3}>
      {renderStatusControl()}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            lg: '1fr 1fr 1fr',
          },
        }}
      >
        {fields.map((item, index) => {
          const total = fields.length

          const isTopLeft = index === 0
          const isTopRight = index === 2
          const isBottomLeft = index === total - (total % 3 || 3)
          const isBottomRight = index === total - 1

          const borderRadiusLg = {
            borderTopLeftRadius: isTopLeft ? '12px' : 0,
            borderTopRightRadius: isTopRight ? '12px' : 0,
            borderBottomLeftRadius: isBottomLeft ? '12px' : 0,
            borderBottomRightRadius: isBottomRight ? '12px' : 0,
          }

          return (
            <StyledBoxCentered
              key={index}
              sx={{
                [theme.breakpoints.up('lg')]: borderRadiusLg,
              }}
            >
              <Label component="span">{item.label}</Label>
              <Value component="span">{item.value}</Value>
            </StyledBoxCentered>
          )
        })}
      </Box>
    </Stack>
  )
}

const StyledBoxCentered = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'row',
  justifyContent: 'space-between',
  padding: '10px 12px',
  border: '1px solid #E4E7EC',
  backgroundColor: '#fff',
}))

const Label = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: '600',
  fontFamily: 'Inter',
  color: '#344054',
})) as typeof Typography

const Value = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: '400',
  fontFamily: 'Inter',
  color: '#475467',
})) as typeof Typography
